import { MouseEvent } from 'react';

const focusable =
  'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable], audio[controls], video[controls], summary, [tabindex^="0"], [tabindex^="1"], [tabindex^="2"], [tabindex^="3"], [tabindex^="4"], [tabindex^="5"], [tabindex^="6"], [tabindex^="7"], [tabindex^="8"], [tabindex^="9"]';

export const getFocusableSelector = () => focusable;

/**
 * True when `el` is in the sequential tab order: matches the platform focusable selector (see
 * {@link getFocusableSelector}),
 * is connected, not under `[inert]`, has `tabIndex >= 0`, and does not have a `disabled` attribute.
 *
 * Use to narrow `querySelectorAll(getFocusableSelector())` to real tab stops (e.g. exclude
 * `tabindex="-1"` buttons) or to test a single element.
 */
export function isSequentialTabStop(el: HTMLElement): boolean {
  if (!el.isConnected) {
    return false;
  }
  if (el.closest('[inert]')) {
    return false;
  }
  if (el.tabIndex < 0) {
    return false;
  }
  if (el.hasAttribute('disabled')) {
    return false;
  }
  try {
    return el.matches(focusable);
  } catch {
    return false;
  }
}

/**
 * Sequential tab stops inside `container` in document order (optionally including the container
 * itself when it is a tab stop). Use for focus-trap–style Tab handling on a single root.
 */
export function getSequentialTabStopsWithin(
  container: HTMLElement
): HTMLElement[] {
  const descendants = Array.from(
    container.querySelectorAll<HTMLElement>(focusable)
  ).filter(el => isSequentialTabStop(el));

  if (isSequentialTabStop(container)) {
    return [container, ...descendants];
  }
  return descendants;
}

export function isFocusable(event: MouseEvent): boolean {
  const target = event.target as HTMLElement;

  return Boolean(target.closest(focusable));
}

/**
 * Same rule as {@link isFocusable}, but for an arbitrary event target (e.g. pointer capture).
 */
export function isFocusableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  try {
    return target.closest(focusable) !== null;
  } catch {
    return false;
  }
}

/**
 * Nearest focusable ancestor of `target` (inclusive), using the same selector as
 * {@link isFocusableTarget}. Returns `null` when none match.
 */
export function nearestFocusableAncestor(
  target: EventTarget | null
): Element | null {
  if (!(target instanceof Element)) {
    return null;
  }
  try {
    return target.closest(focusable);
  } catch {
    return null;
  }
}

export function findFocusableElement(
  startFrom: HTMLElement
): HTMLElement | null {
  if (startFrom.matches(focusable)) {
    return startFrom;
  }

  return startFrom.querySelector(focusable);
}

export const focusNextFocusable = (thisElement: HTMLElement) => {
  // Define a selector for all focusable elements

  // Get all focusable elements in the document
  const focusableElements = Array.from(
    document.querySelectorAll<HTMLElement>(focusable)
  );

  // Find the index of the current element
  const currentIndex = focusableElements.indexOf(thisElement);

  // If the current element is not found or it's the last focusable element, wrap to the first
  const nextIndex = (currentIndex + 1) % focusableElements.length;

  // Focus the next focusable element
  focusableElements[nextIndex]?.focus();
};

export const focusPreviousFocusable = (thisElement: HTMLElement) => {
  const focusableElements = Array.from(
    document.querySelectorAll<HTMLElement>(focusable)
  );

  const currentIndex = focusableElements.indexOf(thisElement);
  const previousIndex =
    (currentIndex - 1 + focusableElements.length) % focusableElements.length;
  focusableElements[previousIndex]?.focus();
};
